import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import CompareMatrixBanner from "../CompareMatrixPricing/CompareMatrixBanner";
import CompareMatrixSection from "../CompareMatrixPricing/CompareMatrixSection";
import CompareMatrixSectionMobile from "../CompareMatrixPricing/CompareMatrixSectionMobile";
import { CompareMatrixPricingType } from "../types/CompareMatrixPricing";
import useMediaQueries from "../../../hooks/useMediaQueries";

const CompareMatrixPricing: React.FC<CompareMatrixPricingType> = ({
  pricingData,
  banner,
}) => {
  const { isMobile } = useMediaQueries();
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [offer, setOffer] = useState("");
  const discountPercent = banner?.buyNowInfo?.buyNow?.discountPercent;
  const calculateDiscount = (orgPrice: number) =>
    ((discountPercent / 100) * orgPrice).toFixed(2);
  const { onClickHandler } = { ...banner };

  const sections = pricingData.compareSections;
  /* We need to shape a data object due to the limited info
   * we recieve from Builder.
   */
  const priceData = [
    {
      name: "Starter",
      orgPrice: pricingData.starterPrice,
      discountPrice: calculateDiscount(pricingData.starterPrice),
    },
    {
      name: "Essential",
      orgPrice: pricingData.essentialPrice,
      discountPrice: calculateDiscount(pricingData.essentialPrice),
    },
    {
      name: "Plus",
      orgPrice: pricingData.plusPrice,
      discountPrice: calculateDiscount(pricingData.plusPrice),
    },
  ];

  // On mobile, the user can select a plan to see features
  const displayMobileSection = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    const el = target?.getElementsByClassName(
      "compare-matrix-mobile__name"
    )[0] as HTMLElement;
    const value = el.innerText.toLowerCase();
    setSelectedPlan(value);
  };

  // Update the offer based on the banner button
  const { includeBuyNowToggle, buyNowInfo } = banner;
  const handleOfferUpdate = (offer: string) => {
    setOffer(offer);
  };

  useEffect(() => {
    if (window.location.hash === "" && includeBuyNowToggle) {
      setOffer("buynow");
    } else {
      const offer = window.location.hash.substring(1);
      setOffer(offer);
    }
  }, []);

  return (
    <section
      id="compare-matrix-top"
      className="compare-matrix-pricing flex justify-center items-center"
    >
      <div className="sm:mx-5 sm:pt-md md:pt-lg max-w-desktop w-full my-0 mx-10 pt-12 pb-md">
        <div className="mb-sm">
          <CompareMatrixBanner
            includeBuyNowToggle={includeBuyNowToggle}
            buyNowInfo={buyNowInfo}
            onOfferChange={handleOfferUpdate}
            onClickHandler={onClickHandler}
          />
        </div>
        {isMobile && (
          <div className="compare-matrix-mobile__plan mb-7 py-xxs">
            <p className="compare-matrix-pricing__font-set text-center mb-xxs">
              Select a plan to see features available:
            </p>
            <div className="compare-matrix-mobile__plan-container flex flex-wrap items-center justify-center p-xxxxs border border-forest-400 rounded-circle">
              {priceData?.map((price, key) => {
                const { name, orgPrice, discountPrice } = price;
                return (
                  <button
                    onClick={displayMobileSection}
                    className={`compare-matrix-mobile__btn relative basis-1/3 py-2 rounded-circle ${
                      selectedPlan === name.toLowerCase()
                        ? "text-forest-900"
                        : "text-forest-700"
                    }`}
                    key={key}
                  >
                    {selectedPlan === name.toLowerCase() && (
                      <motion.span
                        layoutId="pricePill"
                        className="absolute inset-0 bg-sun-600"
                        style={{ borderRadius: 99 }}
                        transition={{
                          type: "spring",
                          bounce: 0.25,
                          duration: 0.5,
                        }}
                      />
                    )}
                    <span className="compare-matrix-mobile__name compare-matrix-pricing__font-set relative block font-medium -mb-1 z-10">
                      {name}
                    </span>
                    {
                      // Change price based on selected banner offer
                      offer === "buynow" ? (
                        <span className="compare-matrix-mobile__price compare-matrix-pricing__font-set relative body--text-eyebrow font-semibold z-10">
                          ${discountPrice}/MO
                        </span>
                      ) : (
                        <span className="compare-matrix-mobile__price compare-matrix-pricing__font-set relative body--text-eyebrow font-semibold z-10">
                          ${orgPrice}/MO
                        </span>
                      )
                    }
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div>
          {sections?.map((section, key) => {
            const { sectionTitle, sectionIcon, features } = section;
            return isMobile ? (
              <CompareMatrixSectionMobile
                sectionTitle={sectionTitle}
                sectionIcon={sectionIcon}
                priceData={priceData}
                features={features}
                planValue={selectedPlan}
                key={key}
              />
            ) : (
              <CompareMatrixSection
                sectionTitle={sectionTitle}
                sectionIcon={sectionIcon}
                priceData={priceData}
                planValue={selectedPlan}
                offer={offer}
                features={features}
                key={key}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CompareMatrixPricing;
